// Phase E1's tutorial prompts on the page (decision 0060: contextual, device-aware, each retiring once its verb is
// used; 0063's cognitive aids): @rpg/meta's `Tutorials` over the tutorials table, a small card at the screen's left
// showing the prompt's words with the glyph of the device used last (the input's glyph table: a key cap, a pad button,
// a touch button; on touch a row may give words in place of a glyph, as the map's minimap tap does).
//   - due: stepped on sim time (prompts wait while the game is paused), their conditions in the story's language plus
//     the page's `play.*` facts (src/meta/facts.ts);
//   - used: inside each sim step the prompts' verbs are read from the input's actions (a press, or the move stick
//     pushed), the look action as the camera consumes it (`noteLook`), and the map whenever its screen shows (M, the
//     pad's select, or the minimap tapped);
//   - shown only in play with no screen or cutscene over it, and with the `meta.hints` setting on; written straight to
//     the DOM when what it shows changes (no Preact render per frame).
// Planted defect `tutorial-never-retires` (@rpg/meta's META_FAULTS.tutorialNeverRetires): a used verb leaves its prompt.
import { Tutorials } from "@rpg/meta";
import type { Conditions } from "@rpg/story";
import type { DeviceFamily, Input, Runtime } from "@rpg/runtime";
import type { SpikeContent } from "../content";
import type { MetaContent } from "./content";
import { tutorialConditions, type PlayState } from "./facts";

const CSS = `
.gv-tip{position:fixed;left:max(16px,env(safe-area-inset-left));top:38%;z-index:12;display:none;align-items:center;gap:10px;max-width:min(46vw,360px);
  padding:9px 14px;border-radius:12px;background:rgba(29,42,31,.86);color:#fbf6e8;font:600 calc(15px * var(--rpg-text-scale,1))/1.3 system-ui,sans-serif;
  box-shadow:0 4px 16px rgba(0,0,0,.25);pointer-events:none}
.gv-tip[data-shown]{display:flex}
.gv-tip kbd{font:700 13px/1 system-ui,sans-serif;padding:4px 7px;border-radius:6px;background:#fbf6e8;color:#1d2a1f;white-space:nowrap}
.gv-tip[data-device=gamepad] kbd{border-radius:999px}
.gv-tip[data-device=touch] kbd{background:transparent;color:#fbf6e8;border:1px solid #fbf6e8}
.gv-tip kbd:empty{display:none}
`;

/** What the tutorials need from the page. */
export interface TutorialDeps {
  runtime: Runtime;
  content: SpikeContent;
  meta: MetaContent;
  input: Input;
  /** family → glyph key → string id (the glyphs table). */
  glyphs: Readonly<Partial<Record<DeviceFamily, Readonly<Record<string, string>>>>>;
  /** The shared condition language (null: only the play facts). */
  conditions: Conditions | null;
  play(): PlayState;
  /** Whether a prompt may show now (play, no screen over it, no cutscene). */
  showing(): boolean;
  /** The `meta.hints` setting. */
  hints(): boolean;
  /** Whether the map screen is up (its verb used). */
  mapOpen(): boolean;
  /** Called when prompts retire (the profile is written). */
  onRetire(ids: string[]): void;
}

/** What the card shows (for the handle and the specs). */
export interface TipState {
  shown: boolean;
  id: string | null;
  verb: string | null;
  device: DeviceFamily | null;
  cap: string;
  text: string;
}

/** Mount the tutorial prompts. */
export function mountTutorials(d: TutorialDeps) {
  if (!document.getElementById("gv-tip-css")) {
    const s = document.createElement("style");
    s.id = "gv-tip-css";
    s.textContent = CSS;
    document.head.appendChild(s);
  }
  const t = d.content.t;
  const conds = tutorialConditions(d.conditions, d.play);
  const tutorials = new Tutorials(d.meta.tutorials, { test: (c) => conds.test(c) });
  const rows = new Map(d.meta.tutorialRows.map((r) => [r.id, r]));
  const kinds = new Map(d.content.registry.rows("actions").map((a) => [a.id, a.kind]));
  const verbs = tutorials.verbs();
  const used: { verb: string; t: number }[] = [];

  const el = document.createElement("div");
  el.className = "gv-tip";
  el.setAttribute("role", "status");
  el.dataset.tip = "";
  const kbd = document.createElement("kbd");
  const words = document.createElement("span");
  el.append(kbd, words);
  document.body.appendChild(el);

  const use = (verb: string): void => {
    const gone = tutorials.use(verb);
    // the uses that met a prompt still waiting (each verb's first, in practice)
    if (!gone.length && !verbs.includes(verb)) return;
    if (used[used.length - 1]?.verb !== verb) used.push({ verb, t: d.runtime.clock.time });
    if (used.length > 64) used.splice(0, 32);
    if (gone.length) d.onRetire(gone);
  };

  // the verbs used inside each sim step, after the game read them (the input's step boundaries are ±1e9)
  d.runtime.on(
    "sim",
    (c) => {
      for (const v of verbs) {
        if (v === "look" || v === "map") continue;
        const kind = kinds.get(v);
        const on = kind === "axis2" ? Math.hypot(d.input.actions.stick(v).x, d.input.actions.stick(v).y) > 0.3 : d.input.actions.pressed(v);
        if (on) use(v);
      }
      tutorials.update(c.dt, d.hints());
    },
    { order: 1e8 + 10, name: "meta: tutorials" },
  );

  // what the card shows: written when it changes
  let key = "";
  let state: TipState = { shown: false, id: null, verb: null, device: null, cap: "", text: "" };
  d.runtime.on(
    "late",
    () => {
      if (d.mapOpen()) use("map");
      const id = tutorials.current();
      const shown = id !== null && d.showing() && d.hints();
      const row = id ? rows.get(id) : undefined;
      const device = d.input.controls.device ?? "keyboardMouse";
      const k = `${shown}|${id}|${device}`;
      if (k === key) return;
      key = k;
      if (!shown || !row) {
        el.removeAttribute("data-shown");
        state = { shown: false, id: null, verb: null, device: null, cap: "", text: "" };
        return;
      }
      const verb = row.verb.id;
      const touchWords = device === "touch" && row.touch ? row.touch.id : null;
      const g = touchWords ? null : d.input.glyph(verb, d.glyphs);
      const cap = g ? t(g.glyph) : "";
      const text = t(touchWords ?? row.text.id);
      kbd.textContent = cap;
      words.textContent = text;
      el.dataset.device = g?.family ?? device;
      el.dataset.verb = verb;
      el.dataset.shown = "";
      state = { shown: true, id, verb, device: g?.family ?? device, cap, text };
    },
    { order: 60, name: "meta: tutorial card" },
  );

  return {
    tutorials,
    /** The look action was used (the camera consumed a look delta). */
    noteLook: (): void => use("look"),
    state: (): TipState => ({ ...state }),
    used: () => used.map((u) => ({ ...u })),
  };
}

/** The mounted tutorials. */
export type TutorialView = ReturnType<typeof mountTutorials>;
